// ============================================================================
// OverlappingBoundaries ViewModel
// ============================================================================

import { useState, useCallback, useMemo } from "react";
import type { OverlappingBoundariesProps, OverlappingBoundariesViewModel } from "./OverlappingBoundaries.types";
import { formatArea, getJobDisplayName } from "../../shared";

/**
 * ViewModel hook for OverlappingBoundaries component
 */
export function useOverlappingBoundariesViewModel(props: OverlappingBoundariesProps): OverlappingBoundariesViewModel {
	const { issues, currentIssueIndex, skippedIssueIds, onDeleteJob } = props;

	// Track which jobs are hidden on the map
	const [hiddenJobIds, setHiddenJobIds] = useState<Set<string>>(new Set());
	// State for delete confirmation dialog
	const [deleteJobId, setDeleteJobId] = useState<string | null>(null);

	// Derived state
	const hasIssues = issues.length > 0;
	const currentIssue = hasIssues ? (issues[currentIssueIndex] ?? null) : null;
	const isSkipped = currentIssue ? skippedIssueIds.has(currentIssue.id) : false;

	// Formatted intersection area
	const intersectionAreaText = useMemo(() => {
		if (!currentIssue) return "";
		return formatArea(currentIssue.intersectionArea);
	}, [currentIssue]);

	// Toggle job visibility
	const toggleJobVisibility = useCallback((jobId: string) => {
		setHiddenJobIds((prev) => {
			const next = new Set(prev);
			if (next.has(jobId)) {
				next.delete(jobId);
			} else {
				next.add(jobId);
			}
			return next;
		});
	}, []);

	// Show all jobs
	const showAllJobs = useCallback(() => {
		setHiddenJobIds(new Set());
	}, []);

	// Hide all jobs
	const hideAllJobs = useCallback(() => {
		if (currentIssue) {
			setHiddenJobIds(new Set(currentIssue.jobs.map((j) => j.id)));
		}
	}, [currentIssue]);

	// Get job display name from jobs in current issue
	const getJobDisplayNameForId = useCallback(
		(jobId: string): string => {
			const job = currentIssue?.jobs.find((j) => j.id === jobId);
			if (!job) return "this job";
			return getJobDisplayName(job);
		},
		[currentIssue],
	);

	// Get source file names for a job (filename only, not full path)
	const getJobSourceFiles = useCallback(
		(jobId: string): string[] => {
			if (!currentIssue?.jobFilePaths) return [];
			const filePaths = currentIssue.jobFilePaths[jobId] || [];
			// Extract filename from path
			return filePaths.map((path) => {
				const parts = path.split(/[/\\]/);
				return parts[parts.length - 1] || path;
			});
		},
		[currentIssue],
	);

	// Handle delete job with confirmation
	const handleDeleteJobClick = useCallback((jobId: string) => {
		setDeleteJobId(jobId);
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (deleteJobId) {
			onDeleteJob(deleteJobId);
		}
		setDeleteJobId(null);
	}, [deleteJobId, onDeleteJob]);

	const handleCancelDelete = useCallback(() => {
		setDeleteJobId(null);
	}, []);

	return {
		hasIssues,
		currentIssue,
		isSkipped,
		intersectionAreaText,
		hiddenJobIds,
		deleteJobId,
		toggleJobVisibility,
		showAllJobs,
		hideAllJobs,
		getJobDisplayName: getJobDisplayNameForId,
		getJobSourceFiles,
		handleDeleteJobClick,
		handleConfirmDelete,
		handleCancelDelete,
	};
}
